import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Brain, CheckCircle, Loader2, Sparkles } from 'lucide-react';

const AnalysisProgressDialog = ({ isVisible, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const stepRefs = useRef({});
  const scrollContainerRef = useRef(null);

  const analysisSteps = useMemo(() => [
    {
      id: 'fetching',
      title: 'Fetching webpage',
      description: 'Downloading and parsing your website content',
      icon: Loader2,
      duration: 1800
    },
    {
      id: 'meta-tags',
      title: 'Analyzing meta tags',
      description: 'Checking title, description, and social media tags',
      icon: CheckCircle,
      duration: 1300
    },
    {
      id: 'headings',
      title: 'Analyzing headings',
      description: 'Reviewing heading structure and hierarchy',
      icon: CheckCircle,
      duration: 1000
    },
    {
      id: 'images',
      title: 'Analyzing images',
      description: 'Checking alt text and image optimization',
      icon: CheckCircle,
      duration: 800
    },
    {
      id: 'structure',
      title: 'Analyzing page structure',
      description: 'Reviewing HTML structure and semantic elements',
      icon: CheckCircle,
      duration: 1300
    },
    {
      id: 'ai-content',
      title: 'AI content analysis',
      description: 'Using artificial intelligence to analyze content quality',
      icon: Brain,
      duration: 2800
    },
    {
      id: 'technical',
      title: 'Technical metrics',
      description: 'Checking performance and technical SEO factors',
      icon: CheckCircle,
      duration: 1000
    },
    {
      id: 'competitive',
      title: 'Competitive insights',
      description: 'AI-powered competitive positioning analysis',
      icon: Sparkles,
      duration: 1800
    },
    {
      id: 'recommendations',
      title: 'Generating AI recommendations',
      description: 'Creating personalized improvement suggestions',
      icon: Brain,
      duration: 2300
    }
  ], []);

    useEffect(() => {
    console.log('ProgressDialog useEffect - isVisible:', isVisible);
    
    if (!isVisible) {
      console.log('ProgressDialog hiding - resetting state');
      setCurrentStep(0);
      setCompletedSteps([]);
      return;
    }

    // Reset state when dialog becomes visible (for new analysis)
    console.log('ProgressDialog starting - resetting state and processing steps');
    setCurrentStep(0);
    setCompletedSteps([]);
    
    const startTime = Date.now();

    const processSteps = async () => {
      console.log('ProgressDialog: Starting to process steps');
      // Always run through all steps for consistent UX
      for (let i = 0; i < analysisSteps.length; i++) {
        console.log(`ProgressDialog: Processing step ${i + 1}/${analysisSteps.length} - ${analysisSteps[i].title}`);
        setCurrentStep(i);
        
        // Wait for the step duration
        await new Promise(resolve => setTimeout(resolve, analysisSteps[i].duration));
        
        // Mark step as completed
        setCompletedSteps(prev => [...prev, analysisSteps[i].id]);
        console.log(`ProgressDialog: Completed step ${i + 1} - ${analysisSteps[i].title}`);
        
        // Add a small delay between steps for better UX (0.1 seconds)
        if (i < analysisSteps.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Keep the dialog visible for at least 3 seconds total for better UX
      const elapsed = Date.now() - startTime;
      const minDuration = 3000;
      if (elapsed < minDuration) {
        await new Promise(resolve => setTimeout(resolve, minDuration - elapsed));
      }

      // Complete the analysis
      if (onComplete) {
        onComplete();
      }
    };

    processSteps();
  }, [isVisible, onComplete, analysisSteps]);

  // Auto-scroll to current step
  useEffect(() => {
    if (currentStep >= 0 && stepRefs.current[`step-${currentStep}`]) {
      // Add a small delay to ensure the DOM has updated
      const scrollTimeout = setTimeout(() => {
        const currentStepElement = stepRefs.current[`step-${currentStep}`];
        const scrollContainer = scrollContainerRef.current;
        
        if (currentStepElement && scrollContainer) {
          // Calculate the scroll position to center the current step
          const containerRect = scrollContainer.getBoundingClientRect();
          const stepRect = currentStepElement.getBoundingClientRect();
          const scrollTop = scrollContainer.scrollTop;
          
          // Calculate the target scroll position to center the step
          const targetScrollTop = scrollTop + stepRect.top - containerRect.top - (containerRect.height / 2) + (stepRect.height / 2);
          
          // Smooth scroll to the target position
          scrollContainer.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: 'smooth'
          });
        }
      }, 100); // 100ms delay for smooth transition
      
      return () => clearTimeout(scrollTimeout);
    }
  }, [currentStep]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white text-center">
          <div className="flex items-center justify-center mb-3">
            <Brain className="w-8 h-8 mr-3" />
            <h2 className="text-2xl font-bold">AI-Powered SEO Analysis</h2>
          </div>
          <p className="text-blue-100 text-lg">
            Our advanced AI is analyzing your website for comprehensive SEO insights
          </p>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ 
                width: `${Math.min((currentStep / (analysisSteps.length - 1)) * 100, 100)}%` 
              }}
            ></div>
          </div>
          <div className="text-center mt-2 text-sm text-gray-600">
            Step {Math.min(currentStep + 1, analysisSteps.length)} of {analysisSteps.length}
            <div className="text-xs text-gray-500 mt-1">
              Progress: {Math.round((currentStep / (analysisSteps.length - 1)) * 100)}%
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div ref={scrollContainerRef} className="p-6 max-h-96 overflow-y-auto">
          <div className="space-y-4">
            {analysisSteps.map((step, index) => {
              const isCurrent = index === currentStep;
              const isCompleted = completedSteps.includes(step.id);
              const IconComponent = step.icon;

              return (
                <div
                  key={step.id}
                  ref={el => stepRefs.current[`step-${index}`] = el}
                  className={`flex items-start space-x-4 p-4 rounded-lg border-2 transition-all duration-300 ${
                    isCurrent
                      ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-200/50'
                      : isCompleted
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isCurrent
                      ? 'bg-blue-500 text-white scale-110'
                      : isCompleted
                      ? 'bg-green-500 text-white scale-105'
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {isCurrent ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : isCompleted ? (
                      <CheckCircle className="w-5 h-5 animate-pulse" />
                    ) : (
                      <IconComponent className="w-5 h-5" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-semibold text-lg ${
                      isCurrent ? 'text-blue-900' : isCompleted ? 'text-green-900' : 'text-gray-700'
                    }`}>
                      {step.title}
                      {isCurrent && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 animate-pulse">
                          Currently analyzing...
                        </span>
                      )}
                    </h3>
                    <p className={`text-sm ${
                      isCurrent ? 'text-blue-700' : isCompleted ? 'text-green-700' : 'text-gray-600'
                    }`}>
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 text-center">
          <div className="flex items-center justify-center text-gray-600 mb-3">
            <Brain className="w-4 h-4 mr-2 text-purple-500" />
            <span className="text-sm">
              Powered by advanced AI technology for accurate and comprehensive analysis
            </span>
          </div>
          
          {/* Scroll to top button */}
          <button
            onClick={() => {
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }
            }}
            className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-800 transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
            Scroll to Top
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisProgressDialog;
